import { useEffect, useMemo, useRef, useState } from "react";
import { GitBranch, Plus, Trash2, Sparkles } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import employeeActionsAPI from "../../../(Admin)EmployeeActions/utils/changesAPIs";
import { PALETTE } from "../../styles/configStyles";
import { getArabicStepName, localizeWorkflowStepName } from "../../../../utils/workflowStepNames";
import ReusableDropdown from "../../../../components/ReusableDropdown";

// Drop-in for the native <select>s in this tab. Always hands back a string,
// exactly like the old `e.target.value` did.
const WfSelect = ({ options, value, onChange, placeholder, disabled, theme, language, compact }) => (
  <ReusableDropdown
    items={options}
    value={value ?? ""}
    onChange={(next) => onChange(String(next))}
    placeholder={placeholder}
    disabled={disabled}
    getItemKey={(option) => option.value}
    getItemLabel={(option) => option.label}
    searchable={options.length > 7}
    theme={theme}
    language={language}
    inputStyle={compact ? { minHeight: "38px", paddingTop: "7px", paddingBottom: "7px" } : undefined}
  />
);

const employeeOptionLabel = (employee, language) =>
  (language === "ar" && employee.arabicName ? employee.arabicName : employee.name) +
  ` (${employee.clientCode || employee.infinityCode})`;

const getModuleOptions = (language) => [
  { value: "ACTION",      label: language === "ar" ? "إجراءات الموظفين" : "Employee Actions" },
  { value: "LEAVE",       label: language === "ar" ? "إجازة"            : "Leave" },
  { value: "ADVANCEMENT", label: language === "ar" ? "سلفة"             : "Advancement" },
  { value: "DOCUMENT",    label: language === "ar" ? "طلب وثيقة"        : "Document Request" },
  { value: "OVERTIME",    label: language === "ar" ? "عمل إضافي"        : "Overtime" },
];

const getActionSubtypeOptions = (language) => [
  { value: "PROMOTION",            label: language === "ar" ? "ترقية"         : "Promotion" },
  { value: "DEMOTION",             label: language === "ar" ? "تخفيض رتبة"    : "Demotion" },
  { value: "TRANSFER",             label: language === "ar" ? "نقل"           : "Transfer" },
  { value: "SALARY_ADJUSTMENT",    label: language === "ar" ? "تعديل راتب"    : "Salary Adjustment" },
  { value: "TERMINATION_EMPLOYER", label: language === "ar" ? "إنهاء عقد"     : "Termination" },
  { value: "RESIGNATION",          label: language === "ar" ? "استقالة"       : "Resignation" },
  { value: "SUSPENSION",           label: language === "ar" ? "إيقاف"         : "Suspension" },
  { value: "REHIRE",               label: language === "ar" ? "إعادة تعيين"   : "Rehire" },
  { value: "REVIEW",               label: language === "ar" ? "مراجعة"        : "Review" },
  { value: "TRAINING",             label: language === "ar" ? "تدريب"         : "Training" },
  { value: "CONTRACT",             label: language === "ar" ? "عقد"           : "Contract" },
  { value: "ROTATION",             label: language === "ar" ? "تناوب"         : "Rotation" },
];

const ACTION_SUBTYPE_ALIASES = {
  promote: "PROMOTION",
  demote: "DEMOTION",
  transfer: "TRANSFER",
  salary_change: "SALARY_ADJUSTMENT",
  salaryadjustment: "SALARY_ADJUSTMENT",
  termination: "TERMINATION_EMPLOYER",
  termination_employer: "TERMINATION_EMPLOYER",
  resignation: "RESIGNATION",
  suspend: "SUSPENSION",
  rehire: "REHIRE",
  review: "REVIEW",
  training: "TRAINING",
  contract: "CONTRACT",
  rotation: "ROTATION",
};

const getSubtypeOptions = (moduleValue, language) => (moduleValue === "ACTION" ? getActionSubtypeOptions(language) : []);

const resolveSubtypeValue = (moduleValue, currentValue) => {
  if (moduleValue !== "ACTION") return "__default__";
  const options = getActionSubtypeOptions("en"); // values only — language doesn't matter here
  if (!currentValue || currentValue === "__default__") return options[0]?.value || "__default__";

  const candidate = String(currentValue).trim();
  const normalized = ACTION_SUBTYPE_ALIASES[candidate.toLowerCase()] || candidate.toUpperCase();
  return options.some((option) => option.value === normalized) ? normalized : options[0]?.value || "__default__";
};

const getAssigneeOptions = (language) => [
  { value: "DIRECT_MANAGER",     label: language === "ar" ? "المدير المباشر" : "Direct Manager" },
  { value: "SECOND_MANAGER",     label: language === "ar" ? "المدير الثاني"  : "Second Manager" },
  { value: "DEPARTMENT_MANAGER", label: language === "ar" ? "مدير القسم"     : "Department Manager" },
  { value: "ADMIN_LEVEL",        label: language === "ar" ? "مستوى إداري"    : "Admin Level" },
  { value: "ADMIN_GENERAL",      label: language === "ar" ? "أي إداري"       : "Any Admin" },
  { value: "EXPLICIT",           label: language === "ar" ? "موافق محدد"     : "Explicit Assignee" },
];

// Reserved departmentId value for the Department Manager type's "Transferred
// department" choice — never a real Department.id (those are DB-generated
// positive integers), so it can share the same departmentId column/pin
// mechanism without a schema change. Means "resolve to the manager of the
// destination department of a Transfer action", as opposed to a real
// department id (a fixed pin) or "" (the employee's own current department).
const TRANSFERRED_DEPARTMENT_SENTINEL = "-1";

const OPERATOR_OPTIONS = [
  { value: "GTE", label: ">=" },
  { value: "LTE", label: "<=" },
  { value: "GT", label: ">" },
  { value: "LT", label: "<" },
  { value: "EQ", label: "=" },
  { value: "NEQ", label: "!=" },
  { value: "IN", label: "IN" },
];

const createStep = (order = 1) => ({
  stepOrder: order,
  stepName: "",
  assigneeType: "DIRECT_MANAGER",
  adminLevelId: "",
  departmentId: "",
  explicitAssigneeInfinityCode: "",
  // Alternate approvers — each one is a FULL assignee-type config, exactly
  // like the step's own assigneeType/adminLevelId/departmentId/
  // explicitAssigneeInfinityCode above, not just a named employee. Any one of
  // them can approve/reject the step in place of the main assignee (first to
  // act completes the step). Unlimited length, applies regardless of the
  // step's own assigneeType.
  alternateAssignees: [],
  conditions: [],
});

/** Default shape for a newly-added alternate row. */
const createAlternate = () => ({
  assigneeType: "DIRECT_MANAGER",
  adminLevelId: "",
  departmentId: "",
  explicitAssigneeInfinityCode: "",
});

/**
 * The backend stores alternateAssignees as JSONB and can hand it back as an
 * array of typed config objects, null, or (defensively) a JSON string.
 * Tolerates the flat employee-code strings this feature originally shipped
 * with by upgrading them to an EXPLICIT config, so an older saved definition
 * still renders correctly. Normalizes every entry to the full shape the UI
 * controls expect so nothing downstream has to guard against missing keys.
 */
const normalizeAlternatesForUI = (value) => {
  if (value === null || value === undefined) return [];
  let raw = value;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      if (typeof entry === "string") {
        const code = entry.trim();
        return code ? { assigneeType: "EXPLICIT", adminLevelId: "", departmentId: "", explicitAssigneeInfinityCode: code } : null;
      }
      if (!entry || typeof entry !== "object") return null;
      const assigneeType = String(entry.assigneeType || "DIRECT_MANAGER").toUpperCase();
      return {
        assigneeType,
        adminLevelId: entry.adminLevelId != null ? String(entry.adminLevelId) : "",
        departmentId: entry.departmentId != null ? String(entry.departmentId) : "",
        explicitAssigneeInfinityCode: entry.explicitAssigneeInfinityCode || "",
      };
    })
    .filter(Boolean);
};

/**
 * Two alternate/main-assignee configs are "equivalent" when they'd resolve to
 * the exact same assignee — mirrors workflowService.js's configsAreEquivalent()
 * so the UI catches a redundant alternate before the save request even goes
 * out.
 */
const assigneeConfigsAreEquivalent = (a, b) => {
  if (!a || !b || a.assigneeType !== b.assigneeType) return false;
  switch (a.assigneeType) {
    case "ADMIN_LEVEL":
      return String(a.adminLevelId || "") === String(b.adminLevelId || "");
    case "EXPLICIT":
      return String(a.explicitAssigneeInfinityCode || "") === String(b.explicitAssigneeInfinityCode || "");
    case "DEPARTMENT_MANAGER":
      return String(a.departmentId || "") === String(b.departmentId || "");
    default:
      return true;
  }
};

/**
 * One-line, human-readable label for an assignee-type config (the step's own
 * main assignee, or one of its alternates) — used by both the preview panel
 * and, via AssigneeTypeFields below, is kept in sync with the assignee-type
 * dropdown options themselves.
 */
const describeAssigneeConfig = (config, { language, adminLevels, departmentList, companyEmployees }) => {
  const type = config?.assigneeType;
  if (type === "ADMIN_LEVEL") {
    const name = adminLevels.find((level) => String(level.id) === String(config.adminLevelId))?.name;
    return `${language === "ar" ? "مستوى إداري" : "Admin Level"}${name ? ` · ${name}` : ""}`;
  }
  if (type === "ADMIN_GENERAL") return language === "ar" ? "أي إداري" : "Any Admin";
  if (type === "DIRECT_MANAGER") return language === "ar" ? "المدير المباشر" : "Direct Manager";
  if (type === "SECOND_MANAGER") return language === "ar" ? "المدير الثاني" : "Second Manager";
  if (type === "DEPARTMENT_MANAGER") {
    if (String(config.departmentId) === TRANSFERRED_DEPARTMENT_SENTINEL) {
      return language === "ar" ? "مدير القسم المنقول إليه" : "Transferred-To Department Manager";
    }
    const name = config.departmentId ? departmentList.find((d) => String(d.id) === String(config.departmentId))?.name : null;
    return `${language === "ar" ? "مدير القسم" : "Department Manager"}${name ? ` · ${name}` : ""}`;
  }
  if (type === "EXPLICIT") {
    const employee = companyEmployees.find((candidate) => candidate.infinityCode === config.explicitAssigneeInfinityCode);
    const name = employee ? (language === "ar" && employee.arabicName ? employee.arabicName : employee.name) : config.explicitAssigneeInfinityCode;
    return `${language === "ar" ? "موافق محدد" : "Explicit Assignee"}${name ? ` · ${name}` : ""}`;
  }
  return type || "";
};

/**
 * The assignee-type sub-form: a type dropdown plus whichever
 * department/admin-level/employee picker that type needs. Used for every
 * alternate row so an alternate can be configured with the exact same
 * richness as the step's own main assignee (see the JSX further down for the
 * main assignee's own, hand-inlined version of this same set of controls).
 */
const AssigneeTypeFields = ({
  value,
  onChange,
  language,
  theme,
  fieldControlStyle,
  loadingEmployees,
  companyEmployees,
  loadingDepartmentList,
  departmentList,
  loadingAdminLevels,
  adminLevels,
  canEdit,
  isTransferWorkflow,
}) => {
  const compactFieldStyle = { ...fieldControlStyle, padding: "8px 10px" };
  const loadingLabel = language === "ar" ? "جارٍ التحميل..." : "Loading...";

  return (
    <div style={{ display: "grid", gap: "8px", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
      <WfSelect
        compact
        options={getAssigneeOptions(language)}
        value={value.assigneeType}
        onChange={(next) =>
          onChange({
            assigneeType: next,
            adminLevelId: next === "ADMIN_LEVEL" ? value.adminLevelId : "",
            departmentId: next === "DEPARTMENT_MANAGER" ? value.departmentId : "",
            explicitAssigneeInfinityCode: next === "EXPLICIT" ? value.explicitAssigneeInfinityCode : "",
          })
        }
        disabled={!canEdit}
        theme={theme}
        language={language}
      />

      {value.assigneeType === "EXPLICIT" && (
        loadingEmployees ? (
          <div style={{ ...compactFieldStyle, display: "flex", alignItems: "center", color: theme === "dark" ? "#cbd5e1" : "#64748b" }}>{loadingLabel}</div>
        ) : (
          <WfSelect
            compact
            options={companyEmployees.map((employee) => ({ value: employee.infinityCode, label: employeeOptionLabel(employee, language) }))}
            value={value.explicitAssigneeInfinityCode || ""}
            onChange={(next) => onChange({ explicitAssigneeInfinityCode: next })}
            placeholder={companyEmployees.length === 0 ? (language === "ar" ? "لا يوجد موظفون متاحون" : "No employees available") : (language === "ar" ? "اختر موظفًا" : "Select employee")}
            disabled={!canEdit || companyEmployees.length === 0}
            theme={theme}
            language={language}
          />
        )
      )}

      {value.assigneeType === "DEPARTMENT_MANAGER" && (
        loadingDepartmentList ? (
          <div style={{ ...compactFieldStyle, display: "flex", alignItems: "center", color: theme === "dark" ? "#cbd5e1" : "#64748b" }}>{loadingLabel}</div>
        ) : (
          <WfSelect
            compact
            options={[
              { value: "", label: language === "ar" ? "قسم الموظف نفسه (تلقائي)" : "Employee's own department (dynamic)" },
              ...(isTransferWorkflow
                ? [{ value: TRANSFERRED_DEPARTMENT_SENTINEL, label: language === "ar" ? "القسم المنقول إليه (وجهة النقل)" : "Transferred department (transfer destination)" }]
                : []),
              ...departmentList.map((dept) => ({ value: String(dept.id), label: dept.name })),
            ]}
            value={value.departmentId ? String(value.departmentId) : ""}
            onChange={(next) => onChange({ departmentId: next })}
            placeholder={language === "ar" ? "قسم الموظف نفسه (تلقائي)" : "Employee's own department (dynamic)"}
            disabled={!canEdit}
            theme={theme}
            language={language}
          />
        )
      )}

      {value.assigneeType === "ADMIN_LEVEL" && (
        loadingAdminLevels ? (
          <div style={{ ...compactFieldStyle, display: "flex", alignItems: "center", color: theme === "dark" ? "#cbd5e1" : "#64748b" }}>{loadingLabel}</div>
        ) : (
          <WfSelect
            compact
            options={adminLevels.map((level) => ({ value: level.id, label: level.name }))}
            value={value.adminLevelId || ""}
            onChange={(next) => onChange({ adminLevelId: next })}
            placeholder={language === "ar" ? "اختر مستوى" : "Select level"}
            disabled={!canEdit}
            theme={theme}
            language={language}
          />
        )
      )}
    </div>
  );
};

const WorkflowsTab = ({
  styles,
  t,
  theme,
  isRTL,
  language,
  profile,
  selectedCompanyName,
  canEditField,
  getFieldStyle,
  showAlert,
  departments = [],
  loadingDepartments = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [seedingDefaults, setSeedingDefaults] = useState(false);
  const [saving, setSaving] = useState(false);
  const [definitions, setDefinitions] = useState([]);
  const [adminLevels, setAdminLevels] = useState([]);
  const [loadingAdminLevels, setLoadingAdminLevels] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [loadingDepartmentList, setLoadingDepartmentList] = useState(false);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [module, setModule] = useState("ACTION");
  const [subType, setSubType] = useState(() => resolveSubtypeValue("ACTION", "__default__"));
  const [scopeType, setScopeType] = useState("company");
  const [targetEmployeeType, setTargetEmployeeType] = useState("__default__");
  const [steps, setSteps] = useState([createStep(1)]);

  // Tracks which "clientName::workingCompany" combos we've already attempted
  // to seed default workflows for in this session, so ensure-defaults is
  // called at most once per company per page load — the backend call is
  // idempotent (no-op if definitions already exist), this just avoids
  // spamming it on every refetch.
  const seededCompaniesRef = useRef(new Set());

  const canEdit = canEditField("leaves");
  // Gates the Department Manager type's "Transferred department" choice: it
  // only resolves meaningfully for an ACTION/TRANSFER workflow (it looks at
  // the transfer's destination department), so it's hidden everywhere else.
  const isTransferWorkflow = module === "ACTION" && subType === "TRANSFER";
  const departmentOptions = useMemo(() => {
    const normalized = (departments || [])
      .map((department) => (typeof department === "string" ? department.trim() : ""))
      .filter(Boolean);

    return [...new Set(normalized)].sort((a, b) => a.localeCompare(b));
  }, [departments]);
  const clientName = profile?.clientName || localStorage.getItem("clientName") || "Infinity";

  const fieldControlStyle = {
    ...styles?.input,
    padding: "11px 14px",
    borderRadius: "12px",
    width: "100%",
  };

  const fetchAdminLevels = async () => {
    if (!selectedCompanyName || !clientName) return;
    setLoadingAdminLevels(true);
    try {
      const response = await apiFetch(
        `${process.env.REACT_APP_BASE_URL}/api/admin-levels/company/${encodeURIComponent(clientName)}/${encodeURIComponent(selectedCompanyName)}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (!response.ok) throw new Error("Failed to load admin levels");
      const data = await response.json();
      setAdminLevels(data.adminLevels || data?.data?.adminLevels || []);
    } catch (err) {
      console.error("Failed to load admin levels", err);
      setAdminLevels([]);
    } finally {
      setLoadingAdminLevels(false);
    }
  };

  const fetchDepartmentList = async () => {
    if (!selectedCompanyName || !clientName) return;
    setLoadingDepartmentList(true);
    try {
      const params = new URLSearchParams({ clientName, workingCompany: selectedCompanyName });
      const response = await apiFetch(`${process.env.REACT_APP_BASE_URL}/api/departments?${params}`, { method: "GET" });
      if (!response.ok) throw new Error("Failed to load departments");
      const body = await response.json();
      setDepartmentList((body?.data || []).map((d) => ({ id: d.id, name: d.name })));
    } catch (err) {
      console.error("Failed to load department list", err);
      setDepartmentList([]);
    } finally {
      setLoadingDepartmentList(false);
    }
  };

  const fetchCompanyEmployees = async () => {
    if (!selectedCompanyName || !clientName) return;
    setLoadingEmployees(true);
    try {
      // Scoped to the currently selected workflow module — the EXPLICIT
      // assignee dropdown should only offer people who actually hold an
      // admin level granting this module (see /assignable-employees).
      const list = await employeeActionsAPI.listWorkflowAssignableEmployees(clientName, selectedCompanyName, module);
      setCompanyEmployees(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load company employees", err);
      setCompanyEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  /**
   * Loads saved workflow definitions for the current company. If the
   * company has none at all yet, seeds the old-system defaults (once per
   * company per session) and refetches so the tab shows them immediately
   * instead of the empty state. Seeding failures are non-fatal — the tab
   * just falls back to the empty state and the admin can still configure
   * workflows manually.
   */
  const fetchDefinitions = async () => {
    if (!selectedCompanyName || !clientName) return;
    setLoading(true);
    try {
      let list = await employeeActionsAPI.listWorkflowDefinitions(clientName, selectedCompanyName);
      list = Array.isArray(list) ? list : [];

      const companyKey = `${clientName}::${selectedCompanyName}`;
      if (list.length === 0 && !seededCompaniesRef.current.has(companyKey)) {
        seededCompaniesRef.current.add(companyKey);
        setSeedingDefaults(true);
        try {
          const seedResult = await employeeActionsAPI.ensureDefaultWorkflowDefinitions(clientName, selectedCompanyName);
          if (seedResult?.created) {
            const reloaded = await employeeActionsAPI.listWorkflowDefinitions(clientName, selectedCompanyName);
            list = Array.isArray(reloaded) ? reloaded : list;
          }
        } catch (seedErr) {
          console.error("Failed to seed default workflows", seedErr);
        } finally {
          setSeedingDefaults(false);
        }
      }

      setDefinitions(list);
    } catch (err) {
      console.error("Failed to load workflow definitions", err);
      setDefinitions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSubType((currentValue) => resolveSubtypeValue(module, currentValue));
  }, [module]);

  useEffect(() => {
    if (scopeType === "department") {
      if (departmentOptions.length > 0 && !departmentOptions.includes(targetEmployeeType)) {
        setTargetEmployeeType(departmentOptions[0]);
      }

      if (departmentOptions.length === 0) {
        setTargetEmployeeType("__default__");
      }
    }
  }, [scopeType, departmentOptions, targetEmployeeType]);

  useEffect(() => {
    fetchAdminLevels();
    fetchDepartmentList();
    fetchDefinitions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyName, clientName]);

  // Separate effect: the assignable-employees list is module-scoped, so it
  // must also refetch whenever the selected module changes (switching from
  // e.g. Leave to Advancement can change who is eligible as an EXPLICIT
  // assignee), not just when the company/client changes.
  useEffect(() => {
    fetchCompanyEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyName, clientName, module]);

  useEffect(() => {
    if (!definitions.length) {
      setSteps([createStep(1)]);
      return;
    }

    const existing = definitions.find((def) => {
      const sameModule = def.module === module;
      const sameSubtype = def.subType === subType;
      const sameScope = def.targetEmployeeType === targetEmployeeType;
      return sameModule && sameSubtype && sameScope;
    });

    if (!existing) {
      setSteps([createStep(1)]);
      return;
    }

    setSteps(
      (existing.steps || []).map((step, index) => ({
        stepOrder: index + 1,
        stepName: step.stepName || "",
        assigneeType: step.assigneeType || "DIRECT_MANAGER",
        adminLevelId: step.adminLevelId || "",
        departmentId: step.departmentId || "",
        explicitAssigneeInfinityCode: step.explicitAssigneeInfinityCode || "",
        alternateAssignees: normalizeAlternatesForUI(step.alternateAssignees),
        conditions: (step.conditions || []).map((condition) => ({
          conditionField: condition.conditionField || "",
          conditionOperator: condition.conditionOperator || "EQ",
          conditionValue: condition.conditionValue || "",
        })),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitions, module, subType, targetEmployeeType]);

  const selectedDefinition = useMemo(() => {
    return definitions.find(
      (def) => def.module === module && def.subType === subType && def.targetEmployeeType === targetEmployeeType
    );
  }, [definitions, module, subType, targetEmployeeType]);

  const updateStep = (index, patch) => {
    setSteps((prev) => prev.map((step, stepIndex) => (stepIndex === index ? { ...step, ...patch } : step)));
  };

  const addStep = () => {
    setSteps((prev) => [...prev, createStep(prev.length + 1)]);
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, stepIndex) => stepIndex !== index).map((step, stepIndex) => ({ ...step, stepOrder: stepIndex + 1 })));
  };

  const addCondition = (stepIndex) => {
    setSteps((prev) => prev.map((step, index) => (index === stepIndex ? { ...step, conditions: [...step.conditions, { conditionField: "", conditionOperator: "EQ", conditionValue: "" }] } : step)));
  };

  const updateCondition = (stepIndex, conditionIndex, patch) => {
    setSteps((prev) => prev.map((step, index) =>
      index === stepIndex
        ? {
          ...step,
          conditions: step.conditions.map((condition, condIndex) => (condIndex === conditionIndex ? { ...condition, ...patch } : condition)),
        }
        : step
    ));
  };

  const removeCondition = (stepIndex, conditionIndex) => {
    setSteps((prev) => prev.map((step, index) => (index === stepIndex ? { ...step, conditions: step.conditions.filter((_, condIndex) => condIndex !== conditionIndex) } : step)));
  };

  const addAlternate = (stepIndex) => {
    setSteps((prev) => prev.map((step, index) => (index === stepIndex ? { ...step, alternateAssignees: [...(step.alternateAssignees || []), createAlternate()] } : step)));
  };

  const updateAlternate = (stepIndex, altIndex, patch) => {
    setSteps((prev) => prev.map((step, index) =>
      index === stepIndex
        ? { ...step, alternateAssignees: step.alternateAssignees.map((existing, i) => (i === altIndex ? { ...existing, ...patch } : existing)) }
        : step
    ));
  };

  const removeAlternate = (stepIndex, altIndex) => {
    setSteps((prev) => prev.map((step, index) => (index === stepIndex ? { ...step, alternateAssignees: step.alternateAssignees.filter((_, i) => i !== altIndex) } : step)));
  };

  const handleSave = async () => {
    if (!selectedCompanyName) {
      showAlert?.(isRTL ? "تنبيه" : "Warning", isRTL ? "يرجى اختيار شركة أولاً" : "Please select a company first", "warning");
      return;
    }

    if (!steps.length) {
      showAlert?.(isRTL ? "تنبيه" : "Warning", isRTL ? "أضف خطوة واحدة على الأقل" : "Please add at least one step", "warning");
      return;
    }

    const unnamedIndex = steps.findIndex((step) => !step.stepName?.trim());
    if (unnamedIndex !== -1) {
      showAlert?.(
        isRTL ? "تنبيه" : "Warning",
        isRTL
          ? `اسم الخطوة مطلوب — أدخل اسماً للخطوة رقم ${unnamedIndex + 1}`
          : `Step name is required — please enter a name for step ${unnamedIndex + 1}`,
        "warning"
      );
      return;
    }

    const trimmedSteps = steps;

    const missingExplicitAssignee = trimmedSteps.find(
      (step) => step.assigneeType === "EXPLICIT" && !step.explicitAssigneeInfinityCode
    );
    if (missingExplicitAssignee) {
      showAlert?.(
        isRTL ? "تنبيه" : "Warning",
        isRTL
          ? `اختر الموظف المكلف للخطوة "${localizeWorkflowStepName(missingExplicitAssignee.stepName, "ar")}"`
          : `Please select the assigned employee for step "${missingExplicitAssignee.stepName}"`,
        "warning"
      );
      return;
    }

    // Each alternate is now a full assignee-type config, so it needs the
    // same "did they actually finish configuring it" check the main assignee
    // gets — missing an admin level for an ADMIN_LEVEL alternate, or an
    // employee for an EXPLICIT one.
    const stepWithIncompleteAlternate = trimmedSteps.find((step) =>
      (step.alternateAssignees || []).some(
        (alt) =>
          (alt.assigneeType === "ADMIN_LEVEL" && !alt.adminLevelId) ||
          (alt.assigneeType === "EXPLICIT" && !alt.explicitAssigneeInfinityCode)
      )
    );
    if (stepWithIncompleteAlternate) {
      showAlert?.(
        isRTL ? "تنبيه" : "Warning",
        isRTL
          ? `أكمل إعداد كل بديل تمت إضافته في الخطوة "${localizeWorkflowStepName(stepWithIncompleteAlternate.stepName, "ar")}" (اختر مستوى إداري أو موظفًا بحسب النوع)`
          : `Please finish configuring every alternate added on step "${stepWithIncompleteAlternate.stepName}" (select an admin level or employee as needed)`,
        "warning"
      );
      return;
    }

    // Reject an alternate that resolves to the exact same assignee as the
    // step's main assignee, or duplicates another alternate on the same
    // step — mirrors workflowService.js's configsAreEquivalent() checks.
    const stepWithRedundantAlternate = trimmedSteps.find((step) => {
      const alternates = step.alternateAssignees || [];
      const mainConfig = {
        assigneeType: step.assigneeType,
        adminLevelId: step.adminLevelId,
        departmentId: step.departmentId,
        explicitAssigneeInfinityCode: step.explicitAssigneeInfinityCode,
      };
      return alternates.some(
        (alt, altIndex) =>
          assigneeConfigsAreEquivalent(alt, mainConfig) ||
          alternates.slice(0, altIndex).some((other) => assigneeConfigsAreEquivalent(alt, other))
      );
    });
    if (stepWithRedundantAlternate) {
      showAlert?.(
        isRTL ? "تنبيه" : "Warning",
        isRTL
          ? `يوجد بديل يطابق الموافق الرئيسي أو بديلاً آخر في الخطوة "${localizeWorkflowStepName(stepWithRedundantAlternate.stepName, "ar")}"`
          : `An alternate matches the main assignee or another alternate on step "${stepWithRedundantAlternate.stepName}"`,
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const payload = {
        clientName,
        workingCompany: selectedCompanyName,
        module,
        subType: module === "ACTION" ? (subType.trim() || "__default__") : "__default__",
        targetEmployeeType: scopeType === "department" ? (targetEmployeeType || "__default__") : "__default__",
        steps: trimmedSteps.map((step, index) => ({
          stepOrder: index + 1,
          stepName: step.stepName.trim(),
          assigneeType: step.assigneeType,
          adminLevelId: step.assigneeType === "ADMIN_LEVEL" && step.adminLevelId && !Number.isNaN(Number(step.adminLevelId)) ? Number(step.adminLevelId) : null,
          departmentId: step.assigneeType === "DEPARTMENT_MANAGER" && step.departmentId && !Number.isNaN(Number(step.departmentId)) ? Number(step.departmentId) : null,
          explicitAssigneeInfinityCode: step.assigneeType === "EXPLICIT" ? (step.explicitAssigneeInfinityCode || null) : null,
          alternateAssignees: (step.alternateAssignees || []).map((alt) => ({
            assigneeType: alt.assigneeType,
            adminLevelId: alt.assigneeType === "ADMIN_LEVEL" && alt.adminLevelId && !Number.isNaN(Number(alt.adminLevelId)) ? Number(alt.adminLevelId) : null,
            departmentId: alt.assigneeType === "DEPARTMENT_MANAGER" && alt.departmentId && !Number.isNaN(Number(alt.departmentId)) ? Number(alt.departmentId) : null,
            explicitAssigneeInfinityCode: alt.assigneeType === "EXPLICIT" ? (alt.explicitAssigneeInfinityCode || null) : null,
          })),
          conditions: (step.conditions || [])
            .filter((condition) => condition.conditionField?.trim())
            .map((condition) => ({
              conditionField: condition.conditionField.trim(),
              conditionOperator: condition.conditionOperator,
              conditionValue: String(condition.conditionValue ?? ""),
            })),
        })),
      };

      await employeeActionsAPI.saveWorkflowDefinition(payload);
      showAlert?.(isRTL ? "نجاح" : "Success", isRTL ? "تم حفظ سير العمل بنجاح" : "Workflow saved successfully", "success");
      await fetchDefinitions();
    } catch (err) {
      console.error("Failed to save workflow definition", err);
      showAlert?.(isRTL ? "خطأ" : "Error", err.message || (isRTL ? "فشل حفظ سير العمل" : "Failed to save workflow"), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: "20px", marginTop: "8px" }}>
      <div style={{ ...styles?.contentSection, padding: "clamp(20px, 3vw, 28px)", display: "grid", gap: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px", flexWrap: "wrap", flexDirection: "row" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 }}>
            <div style={{ ...styles?.sectionIconBadge(PALETTE.primary.base), borderRadius: "12px" }}>
              <GitBranch size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ ...styles?.sectionTitle, marginBottom: 0, paddingBottom: 0, borderBottom: "none" }}>
                {t?.workflows?.title || (language === "ar" ? "سير العمل" : "Workflows")}
              </h3>
              <p style={{ ...styles?.subtitle, marginBottom: 0, marginTop: "4px" }}>
                {language === "ar"
                  ? "أنشئ وتحرّر مسارات الموافقة للشركة أو القسم باستخدام نفس المحرك العالمي للعمليات."
                  : "Create and refine approval chains for the company or a specific department with the same workflow engine used across the platform."}
              </p>
            </div>
          </div>
          <div style={{ ...styles?.companyBadge, padding: "10px 14px", background: theme === "dark" ? `${PALETTE.primary.base}18` : `${PALETTE.primary.base}12`, border: `1px solid ${theme === "dark" ? `${PALETTE.primary.base}33` : `${PALETTE.primary.base}22`}` }}>
            <GitBranch size={16} />
            <span>{selectedCompanyName || (language === "ar" ? "لم يتم اختيار شركة" : "No company selected")}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: "20px", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", alignItems: "start" }}>
        <div style={{ display: "grid", gap: "20px" }}>
          <div style={{ ...styles?.contentSection, padding: "clamp(18px, 3vw, 24px)" }}>
            <div style={{ display: "grid", gap: "16px" }}>
              <div style={{ display: "grid", gap: "14px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                <div style={{ ...styles?.formGroup }}>
                  <label style={styles?.label}>
                    {language === "ar" ? "الوحدة" : "Module"}
                  </label>
                  <WfSelect options={getModuleOptions(language)} value={module} onChange={setModule} theme={theme} language={language} />
                </div>
                {module === "ACTION" && (
                  <div style={{ ...styles?.formGroup }}>
                    <label style={styles?.label}>
                      {language === "ar" ? "نوع المسار" : "Workflow subtype"}
                    </label>
                    <WfSelect options={getSubtypeOptions(module, language)} value={subType} onChange={setSubType} theme={theme} language={language} />
                    <div style={{ fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b", marginTop: "6px" }}>
                      {language === "ar"
                        ? "اختَر نوع الإجراء ليُطابق مسار الموافقة الخاص بالترقية أو النقل أو تعديل الراتب أو غيرها."
                        : "Choose the action type that matches the approval path for promotions, transfers, salary changes, or other employee actions."}
                    </div>
                  </div>
                )}

                {module === "ADVANCEMENT" && (
                  <div style={{ ...styles?.formGroup, gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: "12px", color: theme === "dark" ? "#cbd5e1" : "#475569", background: theme === "dark" ? "rgba(15,23,42,0.6)" : "#f8fafc", padding: "10px 12px", borderRadius: "10px", border: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.9)"}` }}>
                      {language === "ar"
                        ? "معلومة مهمة: في مسار السلف، يُسمح فقط للموافق الذي أتم آخر خطوة موافقة في سير العمل بعملية الصرف. لذلك، احفظ آخر خطوة في المسار على الشخص المسؤول فعليًا عن صرف السلفة."
                        : "Important: for advancement workflows, only the approver who completes the final workflow step can disburse the approved request. Keep the last approval step assigned to the person who is actually expected to release the advancement."}
                    </div>
                  </div>
                )}

                {isTransferWorkflow && (
                  <div style={{ ...styles?.formGroup, gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: "12px", color: theme === "dark" ? "#cbd5e1" : "#475569", background: theme === "dark" ? "rgba(15,23,42,0.6)" : "#f8fafc", padding: "10px 12px", borderRadius: "10px", border: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.9)"}` }}>
                      {language === "ar"
                        ? "معلومة مهمة: عند اختيار \"مدير القسم\" كموافق في مسار النقل، تتوفر خيار قسم إضافي — \"القسم المنقول إليه (وجهة النقل)\" — يُحدَّد تلقائيًا كمدير القسم الجديد الذي سينتقل إليه الموظف، بخلاف اختيار قسم محدد أو قسم الموظف الحالي."
                        : "Important: when a step's assignee is set to \"Department Manager\" on a transfer workflow, its department dropdown gets an extra choice — \"Transferred department (transfer destination)\" — which automatically resolves to the manager of the employee's new department, instead of picking a fixed department or the employee's current one."}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: "grid", gap: "14px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                <div style={{ ...styles?.formGroup }}>
                  <label style={styles?.label}>
                    {language === "ar" ? "النطاق" : "Scope"}
                  </label>
                  <WfSelect
                    options={[
                      { value: "company", label: language === "ar" ? "الشركة بالكامل" : "Company-wide" },
                      { value: "department", label: language === "ar" ? "قسم محدد" : "Specific department" },
                    ]}
                    value={scopeType}
                    onChange={(nextScope) => {
                      setScopeType(nextScope);
                      setTargetEmployeeType(nextScope === "department" ? (departmentOptions[0] || "__default__") : "__default__");
                    }}
                    theme={theme}
                    language={language}
                  />
                </div>
                {scopeType === "department" && (
                  <div style={{ ...styles?.formGroup }}>
                    <label style={styles?.label}>
                      {language === "ar" ? "القسم/النوع" : "Department / employee type"}
                    </label>
                    <WfSelect
                      options={
                        departmentOptions.length > 0
                          ? departmentOptions.map((department) => ({ value: department, label: department }))
                          : [{ value: "__default__", label: language === "ar" ? "لا توجد أقسام متاحة" : "No departments available" }]
                      }
                      value={targetEmployeeType}
                      onChange={setTargetEmployeeType}
                      disabled={loadingDepartments || departmentOptions.length === 0}
                      theme={theme}
                      language={language}
                    />
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: isRTL ? "flex-start" : "flex-end", gap: "10px", flexWrap: "wrap" }}>
                <button type="button" onClick={handleSave} disabled={!canEdit || saving} style={{ ...styles?.saveButton, opacity: !canEdit || saving ? 0.7 : 1, pointerEvents: !canEdit || saving ? "none" : "auto" }}>
                  <Sparkles size={16} />
                  {saving ? (language === "ar" ? "يتم الحفظ..." : "Saving...") : (language === "ar" ? "حفظ سير العمل" : "Save workflow")}
                </button>
              </div>
            </div>
          </div>

          <div style={{ ...styles?.contentSection, padding: "clamp(18px, 3vw, 24px)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexDirection: "row", marginBottom: "14px" }}>
              <h4 style={{ margin: 0, fontSize: "16px", color: theme === "dark" ? "#f8fafc" : "#0f172a", fontWeight: 700 }}>
                {language === "ar" ? "الخطوات" : "Steps"}
              </h4>
              <button type="button" onClick={addStep} disabled={!canEdit} style={{ border: "none", background: theme === "dark" ? `${PALETTE.primary.base}16` : `${PALETTE.primary.base}12`, color: PALETTE.primary.base, borderRadius: "999px", padding: "8px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px", fontWeight: 700 }}>
                <Plus size={14} />
                {language === "ar" ? "إضافة خطوة" : "Add step"}
              </button>
            </div>

            <div style={{ display: "grid", gap: "12px" }}>
              {steps.map((step, index) => (
                <div key={`${step.stepOrder}-${index}`} style={{ border: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.9)"}`, borderRadius: "14px", padding: "14px", display: "grid", gap: "12px", background: theme === "dark" ? "rgba(15,23,42,0.45)" : "rgba(248,250,252,0.9)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: "row" }}>
                    <strong style={{ fontSize: "14px", color: theme === "dark" ? "#f8fafc" : "#0f172a" }}>{language === "ar" ? `الخطوة ${index + 1}` : `Step ${index + 1}`}</strong>
                    {steps.length > 1 && (
                      <button type="button" onClick={() => removeStep(index)} disabled={!canEdit} style={{ border: "none", background: "transparent", color: theme === "dark" ? "#fda4af" : "#dc2626", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                        <Trash2 size={14} />
                        {language === "ar" ? "حذف" : "Remove"}
                      </button>
                    )}
                  </div>

                  <div style={{ ...styles?.formGroup }}>
                    <label style={styles?.label}>{language === "ar" ? "اسم الخطوة" : "Step name"} <span style={{ color: "#ef4444" }}>*</span></label>
                    <input value={step.stepName} onChange={(e) => updateStep(index, { stepName: e.target.value })} placeholder={language === "ar" ? "مثال: مراجعة الموارد البشرية" : "e.g. HR Review"} required style={step.stepName?.trim() ? fieldControlStyle : { ...fieldControlStyle, border: "1px solid #ef4444" }} />
                    {/* The field edits the stored (English) default name; show
                        the Arabic label everyone actually sees for it. */}
                    {language === "ar" && getArabicStepName(step.stepName) && (
                      <div style={{ marginTop: "4px", fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                        {`يظهر للمستخدمين بالعربي: ${getArabicStepName(step.stepName)}`}
                      </div>
                    )}
                  </div>

                  <div style={{ display: "grid", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                    <div style={{ ...styles?.formGroup }}>
                      <label style={styles?.label}>{language === "ar" ? "الموافق/المراجع" : "Assignee"}</label>
                      <WfSelect
                        options={getAssigneeOptions(language)}
                        value={step.assigneeType}
                        onChange={(next) => updateStep(index, { assigneeType: next, adminLevelId: next === "ADMIN_LEVEL" ? step.adminLevelId : "", departmentId: next === "DEPARTMENT_MANAGER" ? step.departmentId : "", explicitAssigneeInfinityCode: next === "EXPLICIT" ? step.explicitAssigneeInfinityCode : "", assignedToAnyAdmin: next === "ADMIN_GENERAL" })}
                        theme={theme}
                        language={language}
                      />
                    </div>
                    {step.assigneeType === "EXPLICIT" && (
                      <div style={{ ...styles?.formGroup }}>
                        <label style={styles?.label}>{language === "ar" ? "الموظف المكلف" : "Assigned employee"}</label>
                        {loadingEmployees ? (
                          <div style={{ ...fieldControlStyle, display: "flex", alignItems: "center", color: theme === "dark" ? "#cbd5e1" : "#64748b" }}>{language === "ar" ? "جارٍ التحميل..." : "Loading..."}</div>
                        ) : (
                          <WfSelect
                            options={companyEmployees.map((employee) => ({ value: employee.infinityCode, label: employeeOptionLabel(employee, language) }))}
                            value={step.explicitAssigneeInfinityCode || ""}
                            onChange={(next) => updateStep(index, { explicitAssigneeInfinityCode: next })}
                            placeholder={companyEmployees.length === 0 ? (language === "ar" ? "لا يوجد موظفون متاحون" : "No employees available") : (language === "ar" ? "اختر موظفًا" : "Select employee")}
                            disabled={!canEdit || companyEmployees.length === 0}
                            theme={theme}
                            language={language}
                          />
                        )}
                      </div>
                    )}
                    {step.assigneeType === "DEPARTMENT_MANAGER" && (
                      <div style={{ ...styles?.formGroup }}>
                        <label style={styles?.label}>{language === "ar" ? "القسم" : "Department"}</label>
                        {loadingDepartmentList ? (
                          <div style={{ ...fieldControlStyle, display: "flex", alignItems: "center", color: theme === "dark" ? "#cbd5e1" : "#64748b" }}>{language === "ar" ? "جارٍ التحميل..." : "Loading..."}</div>
                        ) : (
                          <WfSelect
                            options={[
              { value: "", label: language === "ar" ? "قسم الموظف نفسه (تلقائي)" : "Employee's own department (dynamic)" },
              ...(isTransferWorkflow
                ? [{ value: TRANSFERRED_DEPARTMENT_SENTINEL, label: language === "ar" ? "القسم المنقول إليه (وجهة النقل)" : "Transferred department (transfer destination)" }]
                : []),
              ...departmentList.map((dept) => ({ value: String(dept.id), label: dept.name })),
            ]}
                            value={step.departmentId ? String(step.departmentId) : ""}
                            onChange={(next) => updateStep(index, { departmentId: next })}
                            placeholder={language === "ar" ? "قسم الموظف نفسه (تلقائي)" : "Employee's own department (dynamic)"}
                            theme={theme}
                            language={language}
                          />
                        )}
                      </div>
                    )}
                    {step.assigneeType === "ADMIN_LEVEL" && (
                      <div style={{ ...styles?.formGroup }}>
                        <label style={styles?.label}>{language === "ar" ? "مستوى إداري" : "Admin level"}</label>
                        {loadingAdminLevels ? (
                          <div style={{ ...fieldControlStyle, display: "flex", alignItems: "center", color: theme === "dark" ? "#cbd5e1" : "#64748b" }}>{language === "ar" ? "جارٍ التحميل..." : "Loading..."}</div>
                        ) : (
                          <WfSelect
                            options={adminLevels.map((level) => ({ value: level.id, label: level.name }))}
                            value={step.adminLevelId || ""}
                            onChange={(next) => updateStep(index, { adminLevelId: next })}
                            placeholder={language === "ar" ? "اختر مستوى" : "Select level"}
                            theme={theme}
                            language={language}
                          />
                        )}
                      </div>
                    )}
                  </div>

                  {/*
                    Alternate approvers — each one is a FULL assignee-type
                    config (type + admin level/department/employee, exactly
                    like the step's own main assignee above), and any one of
                    them can approve/reject this step exactly like the main
                    assignee (first to act completes the step). Available
                    regardless of the step's own assigneeType, and unbounded
                    in count.
                  */}
                  <div style={{ display: "grid", gap: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: "row" }}>
                      <span style={{ fontSize: "12px", fontWeight: 700, color: theme === "dark" ? "#f8fafc" : "#0f172a" }}>
                        {language === "ar" ? "الموافقون البدلاء (اختياري)" : "Alternate approvers (optional)"}
                      </span>
                      <button type="button" onClick={() => addAlternate(index)} disabled={!canEdit} style={{ border: "none", background: theme === "dark" ? `${PALETTE.primary.base}16` : `${PALETTE.primary.base}12`, color: PALETTE.primary.base, borderRadius: "999px", padding: "6px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", fontWeight: 700, fontSize: "12px" }}>
                        <Plus size={12} />
                        {language === "ar" ? "إضافة بديل" : "Add alternative"}
                      </button>
                    </div>
                    {(!step.alternateAssignees || step.alternateAssignees.length === 0) && (
                      <div style={{ fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                        {language === "ar"
                          ? "يمكن لأي بديل تتم إضافته الموافقة على هذه الخطوة بدلاً من الموافق الرئيسي — بنفس نوع الموافق (مدير مباشر، مستوى إداري، إلخ)."
                          : "Any alternate added here can approve this step in place of the main approver — configured the same way (Direct Manager, Admin Level, etc.)."}
                      </div>
                    )}
                    {(step.alternateAssignees || []).map((alternate, altIndex) => (
                      <div key={`${step.stepOrder}-alt-${altIndex}`} style={{ display: "grid", gap: "8px", gridTemplateColumns: "1fr auto", alignItems: "start", padding: "8px", borderRadius: "10px", background: theme === "dark" ? "rgba(15,23,42,0.35)" : "rgba(226,232,240,0.4)" }}>
                        <AssigneeTypeFields
                          value={alternate}
                          onChange={(patch) => updateAlternate(index, altIndex, patch)}
                          language={language}
                          theme={theme}
                          fieldControlStyle={fieldControlStyle}
                          loadingEmployees={loadingEmployees}
                          companyEmployees={companyEmployees}
                          loadingDepartmentList={loadingDepartmentList}
                          departmentList={departmentList}
                          loadingAdminLevels={loadingAdminLevels}
                          adminLevels={adminLevels}
                          canEdit={canEdit}
                          isTransferWorkflow={isTransferWorkflow}
                        />
                        <button type="button" onClick={() => removeAlternate(index, altIndex)} disabled={!canEdit} style={{ border: "none", background: "transparent", color: theme === "dark" ? "#fda4af" : "#dc2626", cursor: "pointer", height: "38px" }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "grid", gap: "10px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexDirection: "row" }}>
                      {/*<span style={{ fontSize: "12px", fontWeight: 700, color: theme === "dark" ? "#f8fafc" : "#0f172a" }}>{language === "ar" ? "الشروط (اختياري)" : "Conditions (optional)"}</span>*/}
                      {/*<button type="button" onClick={() => addCondition(index)} disabled={!canEdit} style={{ border: "none", background: "transparent", color: PALETTE.primary.base, cursor: "pointer", fontWeight: 700, padding: 0 }}>
                        + {language === "ar" ? "إضافة شرط" : "Add condition"}
                      </button>*/}
                    </div>
                    {/*{step.conditions.length === 0 && (
                      <div style={{ fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                        {language === "ar" ? "سيتم تطبيق هذه الخطوة على جميع الحالات ما لم تضيف شرطًا." : "This step will apply to every case unless a condition is added."}
                      </div>
                    )}*/}
                    {step.conditions.map((condition, conditionIndex) => (
                      <div key={`${step.stepOrder}-${conditionIndex}`} style={{ display: "grid", gap: "10px", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", alignItems: "end" }}>
                        <div style={{ ...styles?.formGroup }}>
                          <label style={{ ...styles?.label, fontSize: "11px" }}>{language === "ar" ? "الحقل" : "Field"}</label>
                          <input value={condition.conditionField} onChange={(e) => updateCondition(index, conditionIndex, { conditionField: e.target.value })} placeholder="salaryIncreasePercent" style={{ ...fieldControlStyle, padding: "8px 10px" }} />
                        </div>
                        <div style={{ ...styles?.formGroup }}>
                          <label style={{ ...styles?.label, fontSize: "11px" }}>{language === "ar" ? "المشغل" : "Operator"}</label>
                          <WfSelect
                            compact
                            options={OPERATOR_OPTIONS}
                            value={condition.conditionOperator}
                            onChange={(next) => updateCondition(index, conditionIndex, { conditionOperator: next })}
                            theme={theme}
                            language={language}
                          />
                        </div>
                        <div style={{ ...styles?.formGroup }}>
                          <label style={{ ...styles?.label, fontSize: "11px" }}>{language === "ar" ? "القيمة" : "Value"}</label>
                          <input value={condition.conditionValue} onChange={(e) => updateCondition(index, conditionIndex, { conditionValue: e.target.value })} placeholder="10" style={{ ...fieldControlStyle, padding: "8px 10px" }} />
                        </div>
                        <button type="button" onClick={() => removeCondition(index, conditionIndex)} style={{ border: "none", background: "transparent", color: theme === "dark" ? "#fda4af" : "#dc2626", cursor: "pointer", paddingBottom: "4px", height: "38px" }}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: "20px" }}>
          <div style={{ ...styles?.contentSection, padding: "clamp(18px, 3vw, 24px)" }}>
            <h4 style={{ margin: "0 0 10px", fontSize: "16px", color: theme === "dark" ? "#f8fafc" : "#0f172a", fontWeight: 700 }}>{language === "ar" ? "معاينة" : "Preview"}</h4>
            <div style={{ display: "grid", gap: "10px" }}>
              <div style={{ fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                {language === "ar" ? `الوحدة: ${module}` : `Module: ${module}`} · {module === "ACTION"
                  ? (language === "ar" ? `النوع: ${subType || "__default__"}` : `Subtype: ${subType || "__default__"}`)
                  : (language === "ar" ? "النوع: غير مطلوب لهذه الوحدة" : "Subtype: not used for this module")} · {language === "ar" ? `النطاق: ${scopeType === "department" ? "قسم محدد" : "الشركة بالكامل"}` : `Scope: ${scopeType === "department" ? "Specific department" : "Company-wide"}`}
              </div>
              {steps.map((step, index) => (
                <div key={`preview-${index}`} style={{ padding: "10px 12px", borderRadius: "12px", background: theme === "dark" ? "rgba(15,23,42,0.65)" : "#f8fafc", border: `1px solid ${theme === "dark" ? "rgba(148,163,184,0.2)" : "rgba(226,232,240,0.9)"}` }}>
                  <div style={{ fontWeight: 700, marginBottom: "4px", color: theme === "dark" ? "#f8fafc" : "#0f172a" }}>{index + 1}. {localizeWorkflowStepName(step.stepName, language) || (language === "ar" ? "اسم الخطوة" : "Step name")}</div>
                  <div style={{ fontSize: "12px", color: theme === "dark" ? "#cbd5e1" : "#475569" }}>
                    {describeAssigneeConfig(step, { language, adminLevels, departmentList, companyEmployees })}
                  </div>
                  {step.conditions.length > 0 && (
                    <div style={{ marginTop: "6px", fontSize: "12px", color: PALETTE.primary.base }}>
                      {language === "ar" ? "يُطبق فقط إذا:" : "Only applies if:"} {step.conditions.map((condition) => `${condition.conditionField || "field"} ${condition.conditionOperator || "EQ"} ${condition.conditionValue || "value"}`).join(" · ")}
                    </div>
                  )}
                  {(step.alternateAssignees || []).length > 0 && (
                    <div style={{ marginTop: "6px", fontSize: "12px", color: theme === "dark" ? "#cbd5e1" : "#475569" }}>
                      {language === "ar" ? `+ ${step.alternateAssignees.length} بديل: ` : `+ ${step.alternateAssignees.length} alternate${step.alternateAssignees.length > 1 ? "s" : ""}: `}
                      {step.alternateAssignees.map((alt) => describeAssigneeConfig(alt, { language, adminLevels, departmentList, companyEmployees })).join(" · ")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...styles?.contentSection, padding: "clamp(18px, 3vw, 24px)" }}>
            <h4 style={{ margin: "0 0 10px", fontSize: "16px", color: theme === "dark" ? "#f8fafc" : "#0f172a", fontWeight: 700 }}>{language === "ar" ? "التكوين الحالي" : "Current definitions"}</h4>
            {loading ? (
              <div style={{ fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>{language === "ar" ? "جارٍ التحميل..." : "Loading..."}</div>
            ) : seedingDefaults ? (
              <div style={{ fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                {language === "ar" ? "جارٍ إعداد مسارات الموافقة الافتراضية..." : "Setting up default approval workflows..."}
              </div>
            ) : selectedDefinition ? (
              <div style={{ fontSize: "12px", color: theme === "dark" ? "#cbd5e1" : "#475569" }}>
                {language === "ar" ? "تم العثور على تعريف محفوظ لهذا المسار." : "A saved definition for this workflow was found."}
              </div>
            ) : (
              <div style={{ fontSize: "12px", color: theme === "dark" ? "#94a3b8" : "#64748b" }}>
                {language === "ar" ? "لا يوجد تعريف محفوظ بعد. ابدأ من الصفر." : "No saved definition yet. Start from scratch and save to create one."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowsTab;